import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import practiceAnalyticsService from '../../services/practiceAnalyticsService';
import practiceService from '../../services/practiceService';
import { buildProgramRibbonItems } from '../../utils/careProgramUtils';
import PulseIcon from '../../components/Common/PulseIcon/PulseIcon';
import ActionButtons from '../../components/Common/ActionButtons/ActionButtons';
import ListScreenPaginationBar from '../../components/Common/ListScreenPaginationBar/ListScreenPaginationBar';
import PatientReviewCell from '../../components/PatientReviewCell/PatientReviewCell';
import { getMeasurementAlertStatus } from '../../utils/measurementUtils';
import './PracticeAnalyticsDashboard.css';

const ROLE_META = {
  provider: { label: 'Provider', plural: 'Providers', icon: 'fa-solid fa-user-doctor' },
  caregiver: { label: 'Caregiver', plural: 'Caregivers', icon: 'fa-solid fa-user-nurse' },
  admin: { label: 'Practice Admin', plural: 'Admins', icon: 'fa-solid fa-building' }
};

const CPT_ORDER = {
  RPM: ['99454', '99457', '99458'],
  CCM: ['99490', '99439']
};

const waitText = (hours) => {
  const h = Number(hours) || 0;
  if (h < 1) return '< 1 hr';
  if (h < 24) return `${Math.round(h)} hrs`;
  const days = Math.floor(h / 24);
  const rest = Math.round(h % 24);
  return `${days} ${days === 1 ? 'day' : 'days'}${rest ? ` ${rest} hrs` : ''}`;
};

const agoText = (minutes) => {
  if (minutes == null) return '';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return `${hours} hr${hours === 1 ? '' : 's'}${rest ? ` ${rest} min` : ''} ago`;
};

const clock = (row) => {
  if (row.time) return row.time;
  if (!row.dueAt) return '';
  const date = new Date(row.dueAt);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

const alertStatus = (value, min, max, fallbackMin, fallbackMax) => {
  if (value == null || Number(value) <= 0) return null;
  return getMeasurementAlertStatus(value, min ?? fallbackMin, max ?? fallbackMax);
};

const VitalNumber = ({ value, status, suffix }) => {
  if (value == null || status == null) return <span className="pa-na">N/A</span>;
  const cls = status === 'high' ? 'alert-high' : status === 'low' ? 'alert-low' : 'alert-normal';
  return <span className={`pa-vital-num ${cls}`}>{value}{suffix || ''}</span>;
};

const QueuePulse = ({ row }) => {
  if (row.vital !== 'BP' || row.pulse == null) return <span className="pa-na">N/A</span>;
  const pulse = Math.round(row.pulse);
  const targets = row.targets || {};
  const pulseStatus = alertStatus(pulse, targets.pulseMin, targets.pulseMax, 60, 100);
  return (
    <span className="pa-reading-pulse">
      <VitalNumber value={pulse} status={pulseStatus} />
      <PulseIcon isAbnormal={pulseStatus === 'high' || pulseStatus === 'low'} size={14} />
    </span>
  );
};

const QueueSystolic = ({ row }) => {
  if (row.vital !== 'BP' || row.systolic == null) return <span className="pa-na">N/A</span>;
  const value = Math.round(row.systolic);
  const targets = row.targets || {};
  return <VitalNumber value={value} status={alertStatus(value, targets.sysMin, targets.sysMax, 100, 140)} />;
};

const QueueDiastolic = ({ row }) => {
  if (row.vital !== 'BP' || row.diastolic == null) return <span className="pa-na">N/A</span>;
  const value = Math.round(row.diastolic);
  const targets = row.targets || {};
  return <VitalNumber value={value} status={alertStatus(value, targets.diaMin, targets.diaMax, 60, 90)} />;
};

const QueueGlucose = ({ row }) => {
  if (row.vital !== 'BG' || row.glucose == null) return <span className="pa-na">N/A</span>;
  const value = Math.round(row.glucose);
  const targets = row.targets || {};
  return <VitalNumber value={value} status={alertStatus(value, targets.bgMin, targets.bgMax, 60, 110)} />;
};

const QueueWeight = ({ row }) => {
  if (row.vital !== 'WT' || row.weight == null) return <span className="pa-na">N/A</span>;
  const text = (Math.round(row.weight * 10) / 10).toString().replace(/\.0$/, '');
  const targets = row.targets || {};
  return <VitalNumber value={text} status={alertStatus(row.weight, targets.wtMin, targets.wtMax, 66, 220)} />;
};

function BarChart({ labels, abnormal, reviewed }) {
  const width = 560;
  const height = 220;
  const pad = { l: 32, r: 8, t: 10, b: 24 };
  const max = Math.max(5, ...abnormal, ...reviewed);
  const y = (value) => pad.t + (1 - value / max) * (height - pad.t - pad.b);
  const group = (width - pad.l - pad.r) / Math.max(labels.length, 1);
  return (
    <svg className="pa-chart" viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Abnormal readings for the last 14 days">
      {[0, 0.25, 0.5, 0.75, 1].map((step) => {
        const value = Math.round(max * step);
        const yy = y(value);
        return (
          <g key={step}>
            <line x1={pad.l} x2={width - pad.r} y1={yy} y2={yy} stroke="#e0e0e0" strokeDasharray={step ? '3 3' : undefined} />
            <text x={pad.l - 6} y={yy + 3} textAnchor="end" fontSize="10" fill="#666">{value}</text>
          </g>
        );
      })}
      {labels.map((label, index) => {
        const cx = pad.l + group * index + group / 2;
        const bars = [
          { value: abnormal[index] || 0, color: '#0077b6', dx: -7 },
          { value: reviewed[index] || 0, color: '#90e0ef', dx: 3 }
        ];
        return (
          <g key={label}>
            {bars.map((bar) => {
              const top = y(bar.value);
              const bottom = y(0);
              return <rect key={bar.color} x={cx + bar.dx} y={top} width="8" height={Math.max(0, bottom - top)} rx="2" fill={bar.color} />;
            })}
            {index % 2 === 0 && (
              <text x={cx} y={height - 6} textAnchor="middle" fontSize="10" fill="#666">{label}</text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

const QueueAssignCell = ({ row, practiceId, caregivers, providers, onAssigned }) => {
  const patient = useMemo(() => ({
    id: row.patientId,
    patient_table_id: row.patientId,
    practice_id: practiceId,
    abnormal_episode_pending: true,
    is_reviewed: false,
    abnormal_assignee_id: row.assignedToUserId || null,
    abnormal_assigned_to_user_id: row.assignedToUserId || null,
    abnormal_assignee_name: row.assignedToName || '',
    abnormal_assignee_role: row.assignedRole === 'provider' || row.assignedRole === 'caregiver' ? row.assignedRole : ''
  }), [row.patientId, row.assignedToUserId, row.assignedToName, row.assignedRole, practiceId]);

  return (
    <PatientReviewCell
      patient={patient}
      practiceId={practiceId}
      caregiverOptions={caregivers}
      providerOptions={providers}
      compact
      onStatusChange={onAssigned}
    />
  );
};

const PracticeAnalyticsDashboard = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth?.user);
  const { updateBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();
  const [summary, setSummary] = useState(null);
  const [queue, setQueue] = useState([]);
  const [followUps, setFollowUps] = useState([]);
  const [team, setTeam] = useState([]);
  const [cpt, setCpt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [wait, setWait] = useState('lt24');
  const [vital, setVital] = useState('');
  const [assigned, setAssigned] = useState('');
  const [queuePage, setQueuePage] = useState(1);
  const [queuePageSize, setQueuePageSize] = useState('10');
  const [followRole, setFollowRole] = useState('');
  const [showAllFollowUps, setShowAllFollowUps] = useState(false);
  const [teamRole, setTeamRole] = useState('');
  const [cptProgram, setCptProgram] = useState('RPM');
  const [cptCode, setCptCode] = useState('99457');
  const [caregivers, setCaregivers] = useState([]);
  const [providers, setProviders] = useState([]);
  const practiceId = summary?.practice?.id || user?.practice_id;
  const isPracticeAdmin = Number(user?.role_id) === 3;

  useEffect(() => {
    updateBreadcrumbs([{ label: 'Dashboard', path: '/dashboard', active: true }]);
    return () => clearBreadcrumbs();
  }, [updateBreadcrumbs, clearBreadcrumbs]);

  useEffect(() => {
    if (!isPracticeAdmin || !practiceId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const [cgRes, prRes] = await Promise.all([
          practiceService.getPracticeCaregivers(practiceId),
          practiceService.getPracticeProviders(practiceId)
        ]);
        if (cancelled) return;
        const caregiverList = cgRes.data?.data || (Array.isArray(cgRes.data) ? cgRes.data : []);
        const providerList = prRes.data?.data || (Array.isArray(prRes.data) ? prRes.data : []);
        setCaregivers(Array.isArray(caregiverList) ? caregiverList : []);
        setProviders(Array.isArray(providerList) ? providerList : []);
      } catch (error) {
        console.warn('Failed to load caregivers and providers for assignment', error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isPracticeAdmin, practiceId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [summaryRes, queueRes, followRes, teamRes, cptRes] = await Promise.all([
          practiceAnalyticsService.getSummary(),
          practiceAnalyticsService.getQueue(),
          practiceAnalyticsService.getFollowUps(),
          practiceAnalyticsService.getTeam(),
          practiceAnalyticsService.getCpt()
        ]);
        if (cancelled) return;
        setSummary(summaryRes.data?.data || null);
        setQueue(queueRes.data?.data || []);
        setFollowUps(followRes.data?.data?.rows || []);
        setTeam(teamRes.data?.data?.rows || []);
        setCpt(cptRes.data?.data || null);
      } catch (error) {
        if (!cancelled) toast.error(error?.response?.data?.message || 'Could not load the analytics dashboard');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const tiles = summary?.tiles;
  const visibleQueue = useMemo(() => queue.filter((row) => {
    const hours = Number(row.waitHours) || 0;
    if (wait === 'lt24' && hours >= 24) return false;
    if (wait === 'h24to48' && (hours < 24 || hours >= 48)) return false;
    if (wait === 'gt48' && (hours < 48 || hours >= 168)) return false;
    if (wait === 'h24to72' && (hours < 24 || hours >= 72)) return false;
    if (wait === 'd3to7' && (hours < 72 || hours >= 168)) return false;
    if (wait === 'gt168' && hours < 168) return false;
    if (vital && row.vital !== vital) return false;
    if (assigned === 'none' && (row.reviewed || row.assignedRole !== 'none')) return false;
    if (assigned === 'assigned' && (row.reviewed || row.assignedRole === 'none')) return false;
    if (assigned === 'reviewed' && !row.reviewed) return false;
    if (search && !row.name.toLowerCase().includes(search.trim().toLowerCase())) return false;
    return true;
  }), [queue, wait, vital, assigned, search]);

  const pagedQueue = useMemo(() => {
    const size = queuePageSize === 'all' ? visibleQueue.length || 1 : Number(queuePageSize) || 10;
    const start = (Math.max(1, queuePage) - 1) * size;
    return visibleQueue.slice(start, start + size);
  }, [visibleQueue, queuePage, queuePageSize]);

  useEffect(() => {
    setQueuePage(1);
  }, [wait, vital, assigned, search, queuePageSize]);

  const visibleFollowUps = useMemo(() => {
    const rows = followUps.filter((row) => !followRole || row.creatorRole === followRole);
    return showAllFollowUps ? rows : rows.slice(0, 5);
  }, [followUps, followRole, showAllFollowUps]);

  const visibleTeam = useMemo(
    () => team.filter((member) => !teamRole || member.role === teamRole),
    [team, teamRole]
  );

  const programRibbon = useMemo(
    () => buildProgramRibbonItems(summary?.enrolledPrograms || [], summary?.programs || {}),
    [summary?.enrolledPrograms, summary?.programs]
  );

  const cptBlock = cpt?.codes?.[cptCode];
  const openPatient = (patientId) => {
    const practiceId = summary?.practice?.id || user?.practice_id;
    if (practiceId && patientId) navigate(`/practices/${practiceId}/patients/${patientId}`);
  };
  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const applyWaitFilter = (value) => {
    setWait(value);
    scrollTo('pa-queue');
  };
  const waitCaption = wait === 'h24to48'
    ? 'Unreviewed readings · 24–48h'
    : wait === 'gt48'
      ? 'Unreviewed readings · 48h+'
      : wait === 'h24to72'
        ? 'Unreviewed readings · > 24h < 3d'
        : wait === 'd3to7'
          ? 'Unreviewed readings · > 3d < 7d'
          : wait === 'gt168'
            ? 'Unreviewed readings · > 7d'
            : 'Unreviewed readings · < 24h';

  const reviewedDelta = () => {
    const current = tiles?.reviewed24h?.thisWeekPercent;
    const previous = tiles?.reviewed24h?.lastWeekPercent;
    if (current == null || previous == null) return 'Not enough reviewed readings yet';
    const diff = current - previous;
    if (!diff) return 'same as last week';
    return (
      <>
        <b className={diff > 0 ? 'up' : 'num-red'}>
          <i className={`fa-solid ${diff > 0 ? 'fa-arrow-up' : 'fa-arrow-down'}`} /> {Math.abs(diff)}%
        </b>
        {' '}vs last week
      </>
    );
  };

  const missedDelta = () => {
    const diff = (tiles?.missedUploads?.total || 0) - (tiles?.missedUploads?.previous || 0);
    if (!diff) return 'same as last week';
    const improved = diff < 0;
    return (
      <>
        <b className={improved ? 'up' : 'num-red'}>
          <i className={`fa-solid ${diff > 0 ? 'fa-arrow-up' : 'fa-arrow-down'}`} /> {Math.abs(diff)}
        </b>
        {' '}vs last week
      </>
    );
  };

  if (loading) {
    return <div className="pa-page"><div className="pa-loading">Loading analytics…</div></div>;
  }

  const pending = tiles?.pending || {};
  const follows = tiles?.followUps || {};
  const patients = tiles?.patients || {};
  const teamCounts = summary?.teamCounts || {};

  return (
    <div className="pa-page">
      <header className="pa-header">
        <div className="pa-header-main">
          <h2 className="dashboard-title">{summary?.practice?.name || 'Dashboard'}</h2>
          <div className="pa-ribbon">
            {programRibbon.map((prog) => (
              <span key={prog.id} className="pa-ribbon-item">
                <FontAwesomeIcon icon={prog.icon} className="pa-ribbon-icon" style={{ color: prog.color }} />
                <span><b>{prog.count || 0}</b> {prog.short_name || prog.label} patients</span>
              </span>
            ))}
          </div>
        </div>
        <div className="pa-billing">
          <span className="pa-billing-label">RPM billing dates</span>
          <span className="pa-billing-value">{summary?.billing?.label}</span>
        </div>
      </header>

      <section className="pa-tiles">
        <button type="button" className="pa-tile" onClick={() => navigate(`/practices/${summary?.practice?.id || user?.practice_id}?tab=patient`)}>
          <span className="pa-tile-label">Total Patients</span>
          <strong>{patients.total || 0}<small>patients</small></strong>
          <p><b className="up">+{patients.enrolledThisMonth || 0}</b> enrolled this month</p>
          <div className="pa-legend">
            <span>Active <b>{patients.active || 0}</b></span>
            <span>Pend <b>{patients.pending || 0}</b></span>
            <span>Lock <b>{patients.locked || 0}</b></span>
          </div>
        </button>
        <article className="pa-tile accent">
          <button type="button" className="pa-tile-hit" onClick={() => scrollTo('pa-queue')}>
            <span className="pa-tile-label">Pending Review</span>
            <strong>{pending.total || 0}<small>abnormal readings</small></strong>
            <p>Unreviewed readings from the last 7 days</p>
          </button>
          <div className="pa-legend">
            <button type="button" className={wait === 'lt24' ? 'on' : ''} onClick={() => applyWaitFilter('lt24')}>
              &lt;24h <b>{pending.under24 || 0}</b>
            </button>
            <button type="button" className={wait === 'h24to48' ? 'on' : ''} onClick={() => applyWaitFilter('h24to48')}>
              24–48h <b>{pending.between24And48 || 0}</b>
            </button>
            <button type="button" className={wait === 'gt48' ? 'on' : ''} onClick={() => applyWaitFilter('gt48')}>
              48h+ <b>{pending.over48 || 0}</b>
            </button>
          </div>
        </article>
        <article className="pa-tile">
          <span className="pa-tile-label">Reviewed today</span>
          <strong>{tiles?.reviewedToday?.total || 0}<small>readings</small></strong>
          <p>Marked reviewed since midnight</p>
          <div className="pa-legend">
            <span>BP <b>{tiles?.reviewedToday?.bp || 0}</b></span>
            <span>BG <b>{tiles?.reviewedToday?.bg || 0}</b></span>
            <span>Weight <b>{tiles?.reviewedToday?.wt || 0}</b></span>
          </div>
        </article>
        <article className="pa-tile">
          <span className="pa-tile-label">Reviewed in 24 hrs</span>
          <strong>{tiles?.reviewed24h?.thisWeekPercent == null ? '—' : `${tiles.reviewed24h.thisWeekPercent}%`}</strong>
          <p>{reviewedDelta()}</p>
          <div className="pa-legend">
            <span>This week <b>{tiles?.reviewed24h?.thisWeekPercent == null ? '—' : `${tiles.reviewed24h.thisWeekPercent}%`}</b></span>
            <span>Goal <b>95%</b></span>
          </div>
        </article>
        <article className="pa-tile">
          <span className="pa-tile-label">Missed Uploads</span>
          <strong>{tiles?.missedUploads?.total || 0}<small>patients</small></strong>
          <p>{missedDelta()}</p>
          <div className="pa-legend">
            <span>2–3d <b>{tiles?.missedUploads?.d2 || 0}</b></span>
            <span>4–6d <b>{tiles?.missedUploads?.d4 || 0}</b></span>
            <span>7d+ <b>{tiles?.missedUploads?.d7 || 0}</b></span>
          </div>
        </article>
        <article className="pa-tile">
          <span className="pa-tile-label">Billing Eligible</span>
          <strong>{cpt?.eligible?.['99454'] || 0}<small>/ {cpt?.rpmPatients || 0} for 99454</small></strong>
          <p>{summary?.billing?.daysLeft ?? 0} days left in billing period</p>
          <div className="pa-legend">
            <span>99457 <b>{cpt?.eligible?.['99457'] || 0}</b></span>
            <span>99453 <b>{cpt?.eligible?.['99453'] || 0}</b></span>
          </div>
        </article>
      </section>

      <div className="pa-split">
        <section className="pa-card pa-panel" id="pa-queue">
          <div className="pa-card-head">
            <div>
              <h3>Abnormal Readings — Pending Review</h3>
              <p>{waitCaption}</p>
            </div>
            <div className="pa-pills">
              <span>{visibleQueue.length} pending</span>
            </div>
          </div>
          <div className="pa-tools">
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search patient" aria-label="Search patient" />
            <select value={wait} onChange={(event) => setWait(event.target.value)} aria-label="Waiting time">
              <option value="lt24">&lt; 24h</option>
              <option value="h24to48">24–48h</option>
              <option value="gt48">48h+</option>
              <option value="h24to72">&gt; 24h &lt; 3d</option>
              <option value="d3to7">&gt; 3d &lt; 7d</option>
              <option value="gt168">&gt; 7d</option>
            </select>
            <select value={vital} onChange={(event) => setVital(event.target.value)} aria-label="Vital">
              <option value="">Vitals: All</option>
              <option value="BP">BP</option>
              <option value="BG">BG</option>
              <option value="WT">WT</option>
            </select>
            <select value={assigned} onChange={(event) => setAssigned(event.target.value)} aria-label="Assigned to">
              <option value="">All readings</option>
              <option value="none">Unassigned</option>
              <option value="assigned">Assigned</option>
              <option value="reviewed">Reviewed</option>
            </select>
            <button
              type="button"
              className="pa-reset"
              onClick={() => {
                setSearch('');
                setWait('lt24');
                setVital('');
                setAssigned('');
              }}
            >
              Reset
            </button>
          </div>
          <div className="pa-scroll">
            <table className="pa-table pa-queue-table">
              <thead>
                <tr>
                  <th>Patient</th>
                  <th className="pa-num-col">Systolic</th>
                  <th className="pa-num-col">Diastolic</th>
                  <th className="pa-num-col">Pulse</th>
                  <th className="pa-num-col">Glucose</th>
                  <th className="pa-num-col">Weight</th>
                  <th>Waiting</th>
                  <th className="pa-action-col pa-assign-col">Assigned To</th>
                  <th className="pa-action-col">Action</th>
                </tr>
              </thead>
              <tbody>
                {pagedQueue.length === 0 && (
                  <tr><td colSpan={9} className="pa-empty">No abnormal readings need review</td></tr>
                )}
                {pagedQueue.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <button type="button" className="pa-name" onClick={() => openPatient(row.patientId)}>{row.name}</button>
                    </td>
                    <td className="pa-num-col"><QueueSystolic row={row} /></td>
                    <td className="pa-num-col"><QueueDiastolic row={row} /></td>
                    <td className="pa-num-col"><QueuePulse row={row} /></td>
                    <td className="pa-num-col"><QueueGlucose row={row} /></td>
                    <td className="pa-num-col"><QueueWeight row={row} /></td>
                    <td>
                      <span className={row.waitHours >= 48 ? 'wait-over' : row.waitHours >= 24 ? 'wait-late' : 'wait-ok'}>{waitText(row.waitHours)}</span>
                    </td>
                    <td className="pa-action-col pa-assign-col">
                      {row.reviewed ? (
                        <span className="pa-reviewed-mark">Reviewed</span>
                      ) : isPracticeAdmin ? (
                        <QueueAssignCell
                          row={row}
                          practiceId={practiceId}
                          caregivers={caregivers}
                          providers={providers}
                          onAssigned={(patientId, patch) => {
                            setQueue((rows) => rows.map((item) => {
                              if (Number(item.patientId) !== Number(patientId)) return item;
                              const cleared = patch.abnormal_assignee_id == null;
                              return {
                                ...item,
                                assignedToUserId: cleared ? null : Number(patch.abnormal_assignee_id) || null,
                                assignedToName: cleared ? '' : (patch.abnormal_assignee_name || item.assignedToName),
                                assignedRole: cleared ? 'none' : (patch.abnormal_assignee_role || item.assignedRole)
                              };
                            }));
                          }}
                        />
                      ) : (
                        row.assignedToName || 'Unassigned'
                      )}
                    </td>
                    <td className="pa-action-col">
                      <ActionButtons
                        onView={() => openPatient(row.patientId)}
                        hideEdit
                        hideDelete
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ListScreenPaginationBar
            entriesValue={queuePageSize}
            onEntriesChange={setQueuePageSize}
            currentPage={queuePage}
            totalRecords={visibleQueue.length}
            onPageChange={setQueuePage}
          />
        </section>

        <section className="pa-card pa-panel" id="pa-followups">
          <div className="pa-card-head">
            <div>
              <h3>Today&apos;s Follow Ups</h3>
              <p>Latest created · filter by who created them</p>
            </div>
            <span className="pa-total"><b>{follows.total || 0}</b> today</span>
          </div>
          <div className="pa-chips">
            <button type="button" className={!followRole ? 'on' : ''} onClick={() => setFollowRole('')}>All <em>{followUps.length}</em></button>
            {Object.keys(ROLE_META).map((key) => (
              <button key={key} type="button" className={followRole === key ? 'on' : ''} onClick={() => setFollowRole(key)}>
                {ROLE_META[key].plural} <em>{follows.by?.[key] || 0}</em>
              </button>
            ))}
          </div>
          <ul className="pa-fu-list">
            {visibleFollowUps.length === 0 && <li className="pa-empty">No follow ups for today</li>}
            {visibleFollowUps.map((row) => (
              <li key={row.id} className={row.done ? 'done' : row.overdue ? 'over' : ''}>
                <div className="pa-fu-time">{clock(row)}</div>
                <div>
                  <button type="button" className="pa-name" onClick={() => openPatient(row.patientId)}>{row.patientName}</button>
                  {row.done ? <span className="pa-status done">Done</span> : null}
                  <p>{row.task}</p>
                  <small>
                    {ROLE_META[row.creatorRole] ? ROLE_META[row.creatorRole].label : 'Staff'} {row.creatorName}
                    {row.minutesAgo != null ? ` · ${agoText(row.minutesAgo)}` : ''}
                  </small>
                </div>
              </li>
            ))}
          </ul>
          {followUps.length > 5 && (
            <button type="button" className="pa-seeall" onClick={() => setShowAllFollowUps((open) => !open)}>
              {showAllFollowUps ? 'Show latest 5' : `See all ${followUps.length} follow ups`}
            </button>
          )}
        </section>
      </div>

      <h3 className="pa-block">Trends &amp; team performance</h3>
      <div className="pa-grid">
        <section className="pa-card">
          <div className="pa-card-head">
            <div>
              <h3>Abnormal Readings — Last 14 Days</h3>
              <p>New abnormal readings compared with readings reviewed each day</p>
            </div>
          </div>
          <div className="pa-card-body">
            <div className="pa-chart-legend"><span><i className="abn" />New abnormal</span><span><i className="rev" />Reviewed</span></div>
            <BarChart
              labels={(summary?.trend || []).map((point) => point.date)}
              abnormal={(summary?.trend || []).map((point) => point.abnormal)}
              reviewed={(summary?.trend || []).map((point) => point.reviewed)}
            />
          </div>
        </section>
        <section className="pa-card">
          <div className="pa-card-head">
            <div>
              <h3>Pending Review by Waiting Time</h3>
              <p>How long unreviewed abnormal readings have been waiting</p>
            </div>
          </div>
          <div className="pa-card-body">
            {(summary?.waitBars || []).map((bar) => (
              <div className="pa-hbar" key={bar.label}>
                <div>{bar.label}<small>{bar.sub}</small></div>
                <div className="pa-track"><span style={{ width: `${pending.total ? (bar.count / pending.total) * 100 : 0}%`, background: bar.label.startsWith('Less') ? '#0077b6' : '#C4162E' }} /></div>
                <b>{bar.count}</b>
              </div>
            ))}
            <div className="pa-foot">
              <div><b>{tiles?.reviewed24h?.thisWeekHours == null ? '—' : `${tiles.reviewed24h.thisWeekHours} hrs`}</b>Avg time to review · this week</div>
              <div><b>{tiles?.reviewed24h?.lastWeekHours == null ? '—' : `${tiles.reviewed24h.lastWeekHours} hrs`}</b>Avg time to review · last week</div>
              <div><b>{tiles?.reviewed24h?.thisWeekPercent == null ? '—' : `${tiles.reviewed24h.thisWeekPercent}%`}</b>Reviewed within 24 hrs</div>
            </div>
          </div>
        </section>
      </div>

      <div className="pa-grid">
        <section className="pa-card" id="pa-team">
          <div className="pa-card-head">
            <div>
              <h3>Care Team</h3>
              <p>{team.length} staff · {teamCounts.provider || 0} providers · {teamCounts.caregiver || 0} caregivers · {teamCounts.admin || 0} admins</p>
            </div>
          </div>
          <div className="pa-chips">
            <button type="button" className={!teamRole ? 'on' : ''} onClick={() => setTeamRole('')}>All <em>{team.length}</em></button>
            {Object.keys(ROLE_META).map((key) => (
              <button key={key} type="button" className={teamRole === key ? 'on' : ''} onClick={() => setTeamRole(key)}>
                {ROLE_META[key].plural} <em>{teamCounts[key] || 0}</em>
              </button>
            ))}
          </div>
          <div className="pa-scroll short">
            <table className="pa-table pa-queue-table pa-team-table">
              <thead>
                <tr>
                  <th>Staff Name</th>
                  {!teamRole && <th className="pa-num-col">Role</th>}
                  {teamRole !== 'admin' && <th className="pa-num-col">Pending</th>}
                  {teamRole !== 'admin' && <th className="pa-num-col">Reviewed</th>}
                  <th className="pa-num-col">Follow Ups</th>
                </tr>
              </thead>
              <tbody>
                {visibleTeam.length === 0 && (
                  <tr>
                    <td colSpan={2 + (!teamRole ? 1 : 0) + (teamRole === 'admin' ? 0 : 2)} className="pa-empty">No staff found</td>
                  </tr>
                )}
                {visibleTeam.map((member) => (
                  <tr key={member.id}>
                    <td>{member.name}</td>
                    {!teamRole && (
                      <td className="pa-num-col"><span className={`pa-role ${member.role}`}>{ROLE_META[member.role]?.label || member.role}</span></td>
                    )}
                    {teamRole !== 'admin' && <td className="pa-num-col">{member.pending ?? 0}</td>}
                    {teamRole !== 'admin' && <td className="pa-num-col rev">{member.reviewedToday ?? 0}</td>}
                    <td className="pa-num-col">{member.followUpsTotal ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="pa-card">
          <div className="pa-card-head">
            <div>
              <h3>CPT Progress — Near Completion</h3>
              <p><b>{cptCode}</b> · {cptBlock?.description || ''} · {cpt?.daysLeft ?? 0} days left in billing period</p>
            </div>
            <div className="pa-toggle">
              {['RPM', 'CCM'].map((program) => (
                <button
                  key={program}
                  type="button"
                  className={cptProgram === program ? 'on' : ''}
                  onClick={() => {
                    setCptProgram(program);
                    setCptCode(CPT_ORDER[program][0]);
                  }}
                >
                  {program}
                </button>
              ))}
            </div>
          </div>
          <div className="pa-chips">
            {CPT_ORDER[cptProgram].map((code) => (
              <button key={code} type="button" className={cptCode === code ? 'on' : ''} onClick={() => setCptCode(code)}>
                {code} <em>{cpt?.codes?.[code]?.rows?.length || 0}</em>
              </button>
            ))}
          </div>
          <div className="pa-scroll short">
            <table className="pa-table pa-queue-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th className="pa-num-col">{cptBlock?.kind === 'days' ? 'Reading Days' : 'Service Time'}</th>
                  <th className="pa-num-col">{cptBlock?.kind === 'days' ? 'Days Needed' : 'Remaining'}</th>
                  <th className="pa-num-col">Caregiver</th>
                </tr>
              </thead>
              <tbody>
                {(cptBlock?.rows || []).length === 0 && <tr><td colSpan={4} className="pa-empty">No patients are close to this code</td></tr>}
                {(cptBlock?.rows || []).map((row) => {
                  const left = (cptBlock?.goal || 0) - row.value;
                  return (
                    <tr key={`${cptCode}-${row.patientId}`}>
                      <td><button type="button" className="pa-name" onClick={() => openPatient(row.patientId)}>{row.name}</button></td>
                      <td className="pa-num-col">{row.value} / {cptBlock?.goal}{cptBlock?.kind === 'min' ? ' min' : ''}</td>
                      <td className="pa-num-col"><span className={left <= 5 ? 'left good' : 'left warn'}>{left} {cptBlock?.kind === 'days' ? 'days' : 'min'}</span></td>
                      <td className="pa-num-col">{row.caregiver || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
};

export default PracticeAnalyticsDashboard;
